import { useEffect, useRef, useState } from "react";
import { Platform, View } from "react-native";

type ObserverCallback = () => void;

type SharedObserverState = {
  observer: IntersectionObserver;
  callbacks: Map<Element, ObserverCallback>;
};

let sharedObserverState: SharedObserverState | null = null;

function ensureSharedObserver(): SharedObserverState | null {
  if (Platform.OS !== "web" || typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
    return null;
  }
  if (sharedObserverState) return sharedObserverState;

  const callbacks = new Map<Element, ObserverCallback>();
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry, index) => {
        if (!entry.isIntersecting) return;
        const cb = callbacks.get(entry.target);
        if (!cb) return;
        const delay = index * 80;
        window.setTimeout(cb, delay);
      });
    },
    // rootMargin pulls the trigger forward by 240px so a section starts its
    // reveal BEFORE it scrolls into view. The previous threshold-only setting
    // waited until a section was 7% on-screen, which on a tall block (e.g.
    // For Students at ~1100px) meant the in-animation began only after the
    // user was mid-section — perceived as "the white block loads in too
    // slowly". With rootMargin: "240px 0px" the fade is already complete by
    // the time the section's top edge reaches the viewport.
    { threshold: 0, rootMargin: "240px 0px" }
  );

  sharedObserverState = { observer, callbacks };
  return sharedObserverState;
}

function toElement(refValue: unknown): Element | null {
  if (!refValue || typeof refValue !== "object") return null;
  const maybeElement = refValue as { nodeType?: number; getBoundingClientRect?: () => unknown };
  if (typeof maybeElement.nodeType === "number" && typeof maybeElement.getBoundingClientRect === "function") {
    return refValue as Element;
  }
  return null;
}

export function useScrollReveal() {
  const ref = useRef<View>(null);
  const [isVisible, setIsVisible] = useState(Platform.OS !== "web");

  useEffect(() => {
    if (Platform.OS !== "web") {
      setIsVisible(true);
      return;
    }

    const state = ensureSharedObserver();
    const element = toElement(ref.current);
    if (!state || !element) {
      setIsVisible(true);
      return;
    }

    const reveal = () => {
      setIsVisible(true);
      state.observer.unobserve(element);
      state.callbacks.delete(element);
    };

    state.callbacks.set(element, reveal);
    state.observer.observe(element);

    return () => {
      state.observer.unobserve(element);
      state.callbacks.delete(element);
    };
  }, []);

  return { ref, isVisible };
}
